import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid, TextField, Typography } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import MomentUtils from '@date-io/moment';
import moment from 'moment';
import { backendDateFormat, fieldDateFormat } from '../../../../constants';
import { checkApiStatus } from '../../../../utils_v2';
import { trackActivity } from '../../../../utils/mixpanel';
import { globalGetService, globalPostService, globalPutService } from '../../../../utils_v2/globalApiServices';
import { createTransitionGuardApi, updateTransitionGuardApi } from '../apiService';
import { getLesseesListApi } from '../../program_planner/apiService';
import { REPORT_STATUS_OPTIONS } from '../../program_planner/constants';
import {
  GUARD_TYPE_OPTIONS, FIELD_CHECK_FIELD_OPTIONS, FIELD_CHECK_OPERATOR_OPTIONS,
  FIELD_CHECK_OPERATORS_BY_VALUE_TYPE, NO_VALUE_OPERATORS, SET_FIELD_VALUE_TYPE, INVOICE_STATUS_OPTIONS,
} from '../constants';

const SELECT_VALUE_OPTIONS = {
  report_status_records: REPORT_STATUS_OPTIONS,
  report_status_physical: REPORT_STATUS_OPTIONS,
  invoice_status: INVOICE_STATUS_OPTIONS,
};

const errorMessage = (response) => response?.data?.message || 'Unable to save the guard due to a technical error.';

const valueTypeFor = (field) => SET_FIELD_VALUE_TYPE[field?.id] || 'text';

const operatorsFor = (field) => {
  const valueType = valueTypeFor(field);
  const comparisonOperators = FIELD_CHECK_OPERATORS_BY_VALUE_TYPE[valueType] || [];
  const allowedIds = [...comparisonOperators, ...NO_VALUE_OPERATORS];
  return FIELD_CHECK_OPERATOR_OPTIONS.filter((option) => allowedIds.includes(option.id));
};

const isValueEmpty = (valueType, value) => {
  if (valueType === 'select' || valueType === 'select-async') return !value;
  return !value || !String(value).trim();
};

const OptionField = ({ label, options, value, onChange, required = true, error = '', onFocus }) => (
  <Autocomplete
    options={options}
    getOptionLabel={(option) => option?.label}
    getOptionSelected={(option, current) => option.id === current.id}
    value={value || null}
    onChange={(event, newValue) => onChange(newValue || null)}
    renderInput={(params) => (
      <TextField
        {...params}
        required={required}
        label={label}
        variant="outlined"
        size="small"
        InputLabelProps={{ shrink: true }}
        error={!!error}
        helperText={error || ''}
        onFocus={onFocus}
      />
    )}
  />
);

const optionFor = (options, value) => {
  if (!value) return null;
  const id = typeof value === 'object' ? value?.id : value;
  return options.find((option) => option.id === id) || null;
};

const TransitionGuardFormDialog = ({ transitionId, addEditGuard, onToggleModal, getResponseBack, enqueueSnackbar }) => {
  const isEdit = addEditGuard?.mode === 'edit';
  const guard = addEditGuard?.data;
  const initialFieldName = isEdit ? optionFor(FIELD_CHECK_FIELD_OPTIONS, guard?.field_name) : null;
  const initialValueType = valueTypeFor(initialFieldName);
  const [data, setData] = useState({
    name: isEdit ? (guard?.name || '') : '',
    guardType: isEdit ? optionFor(GUARD_TYPE_OPTIONS, guard?.guard_type) || GUARD_TYPE_OPTIONS[0] : GUARD_TYPE_OPTIONS[0],
    fieldName: initialFieldName,
    operator: isEdit ? optionFor(operatorsFor(initialFieldName), guard?.operator) : null,
    value: isEdit && guard?.value != null
      ? (initialValueType === 'select' || initialValueType === 'select-async'
        ? optionFor(SELECT_VALUE_OPTIONS[initialFieldName?.id] || [], guard.value)
        : String(guard.value))
      : '',
    minCount: isEdit && guard?.min_count != null ? String(guard.min_count) : '',
    errorMsg: isEdit ? (guard?.error_message || '') : '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState({});
  const [lessees, setLessees] = useState([]);

  const valueType = valueTypeFor(data.fieldName);
  const operatorOptions = operatorsFor(data.fieldName);

  useEffect(() => {
    if (data.fieldName?.id !== 'lessee_id') return;
    globalGetService(getLesseesListApi()).then((response) => {
      if (checkApiStatus(response)) {
        const list = response?.data?.data?.lessees || [];
        const options = list.map((lessee) => ({ id: lessee.id, label: lessee.name || lessee.label }));
        setLessees(options);
        setData((prev) => (
          prev.fieldName?.id === 'lessee_id' && prev.value && typeof prev.value !== 'object'
            ? { ...prev, value: optionFor(options, prev.value) }
            : prev
        ));
      }
    });
  }, [data.fieldName?.id]);

  const updateData = (patch) => setData((prev) => ({ ...prev, ...patch }));

  const clearError = (key) => setError((prev) => (prev[key] ? { ...prev, [key]: '' } : prev));

  const handleFieldNameChange = (value) => {
    updateData({ fieldName: value, operator: null, value: '' });
  };

  const handleOperatorChange = (value) => {
    updateData({ operator: value, value: '' });
  };

  const needsValue = data.guardType?.id === 'field_check' && data.operator && !NO_VALUE_OPERATORS.includes(data.operator?.id);

  const handleSave = () => {
    const validationInputs = {
      name: data.name?.trim() ? '' : 'Please enter Name',
      guardType: data.guardType ? '' : 'Please select Guard Type',
      fieldName: data.guardType?.id === 'field_check' && !data.fieldName ? 'Please select Field' : '',
      operator: data.guardType?.id === 'field_check' && !data.operator ? 'Please select Operator' : '',
      value: needsValue && isValueEmpty(valueType, data.value) ? 'Please enter Value' : '',
      minCount: data.guardType?.id === 'asset_count' && String(data.minCount).trim() === '' ? 'Please enter Minimum Asset Count' : '',
    };
    if (!Object.keys(validationInputs).every((key) => validationInputs[key] === '')) {
      setError(validationInputs);
      trackActivity('Project Management', {
        event_type: 'Add/Update Transition Guard Failed',
        page_title: 'Planner Tracker Add/Update Transition Guard',
        transition_id: transitionId || '',
        guard_id: guard?.id || '',
        error_source: 'Frontend',
        error: validationInputs,
      });
      return;
    }

    setSaving(true);
    const guardValue = valueType === 'select' || valueType === 'select-async'
      ? data.value?.id
      : valueType === 'date'
        ? (data.value ? moment(data.value).format(backendDateFormat) : '')
        : data.value?.trim();
    const payload = {
      name: data.name,
      guard_type: data.guardType?.id,
      error_message: data.errorMsg?.trim(),
      ...(data.guardType?.id === 'field_check' ? {
        field_name: data.fieldName?.id,
        operator: data.operator?.id,
        ...(needsValue ? { value: guardValue } : {}),
      } : {}),
      ...(data.guardType?.id === 'asset_count' ? { min_count: Number(data.minCount) } : {}),
    };
    const request = isEdit
      ? globalPutService(updateTransitionGuardApi(guard?.id), payload)
      : globalPostService(createTransitionGuardApi(transitionId), payload);
    request.then((response) => {
      setSaving(false);
      if (checkApiStatus(response)) {
        enqueueSnackbar(isEdit ? 'Guard updated' : 'Guard added', { variant: 'success' });
        getResponseBack();
        trackActivity('Project Management', {
          event_type: `${isEdit ? 'Update' : 'Add'} Transition Guard Success`,
          page_title: `Planner Tracker ${isEdit ? 'Update' : 'Add'} Transition Guard`,
          transition_id: transitionId || '',
          guard_id: guard?.id || '',
          request_body: payload,
          response_msg: response?.data?.message,
        });
      } else {
        enqueueSnackbar(errorMessage(response), { variant: 'error' });
        trackActivity('Project Management', {
          event_type: `${isEdit ? 'Update' : 'Add'} Transition Guard Failed`,
          page_title: `Planner Tracker ${isEdit ? 'Update' : 'Add'} Transition Guard`,
          transition_id: transitionId || '',
          guard_id: guard?.id || '',
          request_body: payload,
          error_source: 'Backend',
          error_message: errorMessage(response),
        });
      }
    });
  };

  return (
    <Dialog open={!!addEditGuard?.modal} maxWidth="xs" fullWidth>
      <DialogTitle>
        <Typography variant="h6" style={{ fontWeight: 700 }}>{isEdit ? 'Edit Guard' : 'Add Guard'}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              fullWidth required label="Name" variant="outlined" size="small"
              InputLabelProps={{ shrink: true }}
              value={data.name} onChange={(event) => updateData({ name: event.target.value })}
              error={!!error.name}
              helperText={error.name || ''}
              onFocus={() => clearError('name')}
            />
          </Grid>
          <Grid item xs={12}>
            <OptionField
              label="Guard Type"
              options={GUARD_TYPE_OPTIONS}
              value={data.guardType}
              onChange={(value) => updateData({ guardType: value })}
              error={error.guardType}
              onFocus={() => clearError('guardType')}
            />
          </Grid>

          {data.guardType?.id === 'field_check' ? (
            <>
              <Grid item xs={12}>
                <OptionField
                  label="Field"
                  options={FIELD_CHECK_FIELD_OPTIONS}
                  value={data.fieldName}
                  onChange={handleFieldNameChange}
                  error={error.fieldName}
                  onFocus={() => clearError('fieldName')}
                />
              </Grid>
              <Grid item xs={12}>
                <OptionField
                  label="Operator"
                  options={operatorOptions}
                  value={data.operator}
                  onChange={handleOperatorChange}
                  error={error.operator}
                  onFocus={() => clearError('operator')}
                />
              </Grid>
              {needsValue ? (
                <Grid item xs={12}>
                  {valueType === 'date' ? (
                    <MuiPickersUtilsProvider utils={MomentUtils}>
                      <KeyboardDatePicker
                        fullWidth label="Value" format={fieldDateFormat} inputVariant="outlined" margin="dense"
                        required
                        InputLabelProps={{ shrink: true }}
                        value={data.value || null}
                        onChange={(date) => updateData({ value: date || '' })}
                        error={!!error.value}
                        helperText={error.value || ''}
                        onFocus={() => clearError('value')}
                      />
                    </MuiPickersUtilsProvider>
                  ) : valueType === 'select' || valueType === 'select-async' ? (
                    <OptionField
                      label="Value"
                      options={valueType === 'select-async' ? lessees : (SELECT_VALUE_OPTIONS[data.fieldName?.id] || [])}
                      value={data.value}
                      onChange={(value) => updateData({ value })}
                      error={error.value}
                      onFocus={() => clearError('value')}
                    />
                  ) : (
                    <TextField
                      fullWidth required label="Value" variant="outlined" size="small"
                      InputLabelProps={{ shrink: true }}
                      value={data.value} onChange={(event) => updateData({ value: event.target.value })}
                      error={!!error.value}
                      helperText={error.value || ''}
                      onFocus={() => clearError('value')}
                    />
                  )}
                </Grid>
              ) : null}
            </>
          ) : null}

          {data.guardType?.id === 'asset_count' ? (
            <Grid item xs={12}>
              <TextField
                fullWidth label="Minimum Asset Count" type="number" variant="outlined" size="small"
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: 0 }}
                value={data.minCount} onChange={(event) => updateData({ minCount: event.target.value })}
                error={!!error.minCount}
                helperText={error.minCount || ''}
                onFocus={() => clearError('minCount')}
              />
            </Grid>
          ) : null}

          <Grid item xs={12}>
            <TextField
              fullWidth multiline minRows={2} label="Error Message" variant="outlined" size="small"
              InputLabelProps={{ shrink: true }}
              value={data.errorMsg} onChange={(event) => updateData({ errorMsg: event.target.value })}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: '12px 24px' }}>
        <Button onClick={() => onToggleModal()}>Cancel</Button>
        <Button variant="contained" color="primary" disabled={saving} onClick={handleSave}>
          {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Add Guard'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransitionGuardFormDialog;
